import React from "react";
import { getListQueryStatus } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	Button,
	ClassNameProps,
	Column,
	compareObjects,
	compareStringsAsc,
	Heading,
	Modal,
	Row,
	StyleProps,
	toLocalDateString,
} from "@hex-insights/core";
import { EmptyView } from "../../../../../Components/Empty";
import { Icon } from "../../../../../Components/Icon";
import { Tile } from "../../../../../Components/Tile";
import {
	CourseSection,
	CourseSectionNameFragment,
	useCourseSectionCourseAssessmentPreviewSummaryListQuery,
} from "../../../../../Utilities";
import * as ModelComponents from "../../../_General";
import * as CourseAssessmentComponents from "../../../CourseAssessment";
import { CourseSectionGradeBookModal } from "../../../CourseAssessment/CourseSectionGradeBook";
import * as CourseAttendanceRecordComponents from "../../../CourseAttendanceRecord";
import { CourseSectionScheduleTile } from "../../../CourseSectionPeriod";
import styles from "./styles.module.css";

export type BodyProps = {
	courseSection: Pick<CourseSection, "id"> & CourseSectionNameFragment;
};

export function Body({ courseSection }: BodyProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const assessmentFormStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				courseAssessmentTimings: [
					{ courseSectionID: courseSection.id, assignOn: toLocalDateString(new Date()), dueOn: null },
				],
			},
		}),
		[courseSection.id],
	);

	return (
		<Row justify="spaced-start" horizontalSpacing="1rem" style={{ flexGrow: 1 }}>
			<Column
				justify="spaced-start"
				verticalSpacing="1rem"
				className={styles["body__side-bar"]}
				style={{ flexShrink: 0 }}
			>
				<UpcomingAssessmentsTile courseSectionID={courseSection.id} />

				<CourseAttendanceRecordComponents.ControlledCourseSectionReportTile
					courseSection={courseSection}
					date={toLocalDateString(new Date())}
				/>

				<CourseSectionScheduleTile courseSectionID={courseSection.id} headingLevel={3} />

				<ModelComponents.CourseLessonPlanStatusesTile headingLevel={3} />
			</Column>

			<Column justify="spaced-start" verticalSpacing="1rem" style={{ flexGrow: 1 }}>
				<Row justify="space-between">
					<Row justify="spaced-start">
						<GradeBookModalController courseSectionID={courseSection.id} />
						<AssessmentsModalController courseSectionID={courseSection.id} />
						<Button className={styles["body__action-button"]} onClick={() => {}}>
							<Row justify="spaced-start" horizontalSpacing="0.25rem" align="center">
								<Icon.BookOpen size={16} />
								<span>Curriculum</span>
							</Row>
						</Button>
						<Button className={styles["body__action-button"]} onClick={() => {}}>
							<Row justify="spaced-start" horizontalSpacing="0.25rem" align="center">
								<Icon.BookOpen size={16} />
								<span>Lesson Plans</span>
							</Row>
						</Button>
						<Button className={styles["body__action-button"]} onClick={() => {}}>
							<Row justify="spaced-start" horizontalSpacing="0.25rem" align="center">
								<Icon.BookOpen size={16} />
								<span>Resources</span>
							</Row>
						</Button>
					</Row>

					<Button
						variant="primary"
						className={styles["body__action-button"] + " " + styles["body__action-button--primary"]}
						onClick={openModal}
					>
						<Row justify="spaced-start" horizontalSpacing="0.25rem" align="center">
							<Icon.Plus size={16} /> <span>Create New</span>
						</Row>
					</Button>

					<Modal.If condition={isModalOpen}>
						<CourseAssessmentComponents.CourseAssessmentCreateModal
							formStateArgs={assessmentFormStateArgs}
							formNameSuffix={courseSection.id}
							onClose={closeModal}
						/>
					</Modal.If>
				</Row>

				<Tile style={{ flexGrow: 1 }}>
					<Tile.Header>
						<Heading.H3 noMargin>Activity</Heading.H3>
					</Tile.Header>
					<Tile.Body style={{ display: "flex", flexDirection: "column" }}>
						<EmptyView IconComponent={Icon.List} primaryText="Activity" secondaryText="No activity to show" />
					</Tile.Body>
				</Tile>
			</Column>
		</Row>
	);
}

type UpcomingAssessmentsTileProps = {
	courseSectionID: CourseSection["id"];
} & Partial<ClassNameProps & StyleProps>;

function UpcomingAssessmentsTile({ courseSectionID, className, style }: UpcomingAssessmentsTileProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { loading, data, error } = useCourseSectionCourseAssessmentPreviewSummaryListQuery({
		variables: {
			courseSectionID,
			filters: {
				courseAssessmentTimings: [
					{
						courseSectionIDEQ: courseSectionID,
						or: [
							{
								dueOnGTE: toLocalDateString(new Date()),
								dueOnLTE: toLocalDateString(addTimeToDate(new Date(), [1, "week"])),
							},
						],
					},
				],
			},
		},
	});
	const courseAssessments = React.useMemo(
		() =>
			data?.courseAssessmentConnection.edges
				.map(({ node }) => node)
				.sort(compareObjects((e) => e.courseAssessmentTimings[0].dueOn, compareStringsAsc()))
				.slice(0, 3) ?? [],
		[data],
	);
	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "courseAssessmentConnection" });

	return (
		<React.Fragment>
			<Tile className={className} style={style}>
				<Tile.Header>
					<Row justify="space-between" align="center">
						<Heading.H3 noMargin>Upcoming</Heading.H3>

						<Button variant="link" size="small" onClick={openModal}>
							View All
						</Button>
					</Row>
				</Tile.Header>
				<Tile.Body>
					<CourseAssessmentComponents.CourseAssessmentsPreviewTable
						courseAssessments={courseAssessments}
						courseSectionID={courseSectionID}
						queryStatus={queryStatus}
					/>
				</Tile.Body>
			</Tile>

			<Modal.If condition={isModalOpen}>
				<CourseAssessmentComponents.ControlledCourseSectionUpcomingCourseAssessmentsListModal
					courseSectionID={courseSectionID}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type ModalControllerProps = {
	courseSectionID: CourseSection["id"];
};

function GradeBookModalController({ courseSectionID }: ModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button className={styles["body__action-button"]} onClick={openModal}>
				<Row justify="spaced-start" horizontalSpacing="0.25rem" align="center">
					<Icon.BookOpen size={16} />
					<span>Grade Book</span>
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<CourseSectionGradeBookModal courseSectionID={courseSectionID} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

function AssessmentsModalController({ courseSectionID }: ModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button className={styles["body__action-button"]} onClick={openModal}>
				<Row justify="spaced-start" horizontalSpacing="0.25rem" align="center">
					<Icon.NotePencil size={16} />
					<span>Assessments</span>
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<CourseAssessmentComponents.ControlledCourseSectionCourseAssessmentsListModal
					courseSectionID={courseSectionID}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}
